import { Link } from 'react-router-dom';
import { CONTACT_EMAIL } from '../config/legal';

/**
 * Public support page — the Support URL required by App Store Connect, and the
 * place the iOS app's "Get help" link points at. Public on purpose: someone who
 * cannot sign in must still be able to reach it.
 */
export default function Support() {
    return (
        <div className="min-h-screen bg-gray-50 py-10 px-4">
            <div className="max-w-2xl mx-auto bg-white rounded-lg shadow p-8">
                <h1 className="text-3xl font-bold text-gray-900">🎁 Wishful — Support</h1>
                <p className="text-gray-700 mt-3">
                    Wishful is a gift wish-list app: keep a list of things you'd love, share it
                    with your groups, and let people claim gifts without spoiling the surprise.
                </p>

                <section className="mt-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Get in touch</h2>
                    <p className="text-gray-700">
                        Questions, bugs, or anything about your data — email{' '}
                        <a className="text-blue-600 hover:underline" href={`mailto:${CONTACT_EMAIL}`}>
                            {CONTACT_EMAIL}
                        </a>
                        . Please say which device you're on and what you were doing, and we'll get
                        back to you.
                    </p>
                </section>

                <section className="mt-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-3">Common questions</h2>
                    <div className="space-y-4">
                        <Faq q="I forgot my password.">
                            Use <strong>Forgot password?</strong> on the sign-in screen. We'll email
                            you a reset link.
                        </Faq>
                        <Faq q="Why can't I see who bought my gift?">
                            That's deliberate — it's the whole point of Wishful. When someone claims
                            or buys something from your list, we hide it from you so the surprise
                            survives. Other people in the group can see it, so nobody buys the same
                            thing twice.
                        </Faq>
                        <Faq q="How do I invite someone?">
                            Open a group, go to <strong>Members</strong>, and invite them by email.
                            They'll get a link to join. They can accept in the app or on the web.
                        </Faq>
                        <Faq q="How do I delete my account?">
                            In the app, go to <strong>Profile → Delete account</strong>. We'll ask
                            for your password to confirm. Deletion is immediate and permanent — see
                            the <Link to="/privacy" className="text-blue-600 hover:underline">privacy policy</Link>{' '}
                            for exactly what's removed.
                        </Faq>
                        <Faq q="What happens to my pet's wish list if I delete my account?">
                            It's deleted along with your account. Deleting a pet also deletes that
                            pet's wishes.
                        </Faq>
                    </div>
                </section>

                <div className="mt-10 pt-6 border-t border-gray-200 text-sm flex gap-4">
                    <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>
                    <Link to="/login" className="text-blue-600 hover:underline">Sign in</Link>
                </div>
            </div>
        </div>
    );
}

function Faq({ q, children }) {
    return (
        <div>
            <h3 className="font-semibold text-gray-900">{q}</h3>
            <p className="text-gray-700 mt-1">{children}</p>
        </div>
    );
}
